import React from "react";
import ResearchArticle, {
  Body,
  Figure,
  List,
  Notes,
  Points,
  Section,
  Standout,
  Timeline,
} from "../components/portfolio/ResearchArticle";
import { relatedArticles } from "../data/articles";
import { phoneStage } from "../data/articleStages";
import imgInstallmentsLabtesting from "../assets/images/installments-labtesting.webp";
import imgAuthConfirm from "../assets/images/homepage/installments/auth-confirm.webp";
import imgAuthSheetOpen from "../assets/images/homepage/installments/auth-confirm-sheet-open.webp";
import imgAuthResult from "../assets/images/homepage/installments/auth-result.webp";
import imgPostSplit from "../assets/images/homepage/installments/post-split-selector.webp";
import imgAutoSplit from "../assets/images/homepage/installments/auto-split-selector.webp";

// Installments — a bank app that had to double installment revenue. A short
// read meant to earn a conversation: the problem, the move to payment
// authorization (the part told in depth), the two funnel decisions, and where
// the uplift came from. Research detail and the full rollout are for the call.
//
// The bank stays anonymous. +114% is against the previous year's installment
// revenue; the uplift shares (45 / 40 / 10 / 5) must add up to 100.

const ENTRY_POINTS = phoneStage(
  [
    [imgAuthConfirm, 1624],
    [imgPostSplit, 1622],
    [imgAutoSplit, 1624],
  ],
  { ratio: "15 / 8" }
);

const AUTHORIZATION = phoneStage([
  [imgAuthConfirm, 1624],
  [imgAuthSheetOpen, 1624],
  [imgAuthResult, 1622],
]);

const META = [
  { label: "Role", value: "Design lead in a product, design and data triad" },
  { label: "Team", value: "Me, a PM and a data analyst" },
  { label: "Product", value: "A bank’s app, credit-card installments" },
  { label: "Time", value: "One year, against a +100% revenue target" },
];

const OUTCOMES = [
  { value: "+114%", label: "Installment revenue in one year, against the previous year" },
  { value: "40%", label: "Of the uplift from authorize-and-split, the new stream" },
  { value: "~49%", label: "Automatic-installment conversion, up from ~5%" },
];

const SHIFT = [
  {
    title: "Interviews",
    copy: "Motives were mixed — affordability, liquidity, buying now and paying from the next salary — but the moment was shared: people decided at checkout.",
  },
  {
    title: "The closest page the bank owned",
    copy: "Payment authorization: the amount on screen, the charge not yet confirmed, a choice still to make.",
  },
  {
    title: "Proposed, then approved",
    copy: "I proposed offering plans on that page with pay in full still selected by default. The PM and the business approved it.",
  },
  {
    title: "A 5% rollout",
    copy: "With a stop condition on authorization conversion. Authorization conversion held.",
  },
  {
    title: "Within months",
    copy: "The second-largest revenue contributor.",
  },
];

const DECISIONS = [
  {
    title: "Comparable plans",
    copy: "Lab testing put a vertical list of predefined plans ahead of sliders and grids. Selector drop-off fell from ~45% to ~23%; funnel conversion rose from ~37% to ~65%.",
  },
  {
    title: "Transparent pricing",
    copy: "Against pressure to push the longest plans, the variant that showed the most-used months first and kept fees visible won on conversion and on total revenue.",
  },
];

const UPLIFT = [
  { title: "45%", copy: "Post-purchase improvements" },
  { title: "40%", copy: "Authorize-and-split" },
  { title: "10%", copy: "Automatic installments, redesigned the same year" },
  { title: "5%", copy: "Smaller initiatives and campaigns" },
];

const PROOF = [
  "Mapping the decision found revenue the roadmap missed.",
  "Transparent pricing beat pressure tactics, on revenue too.",
  "A small rollout with a stop condition let a risky placement ship.",
];

const NOTES = [
  "The bank is anonymised.",
  "Revenue figures are measured against the previous year’s installment revenue.",
];

function CaseStudyInstallments() {
  return (
    <ResearchArticle
      eyebrow="Case study"
      title="Doubling installment revenue by finding the right moment"
      lead="A bank’s app had to double installment revenue in a year, and a third of the plan was blocked midway. We mapped when people actually decided to split, I proposed a new option at payment authorization, and the year closed at +114%."
      outcomes={OUTCOMES}
      meta={META}
      hero={{
        visual: ENTRY_POINTS,
        alt: "Three ways to split a purchase in the bank app. At payment authorization for 600 euros at Zara Store, pay in full selected with the six- and two-month plans below it. After the purchase, choosing a plan for the same purchase: six, four or two months. And automatically, for every future purchase over 300 euros",
        label: "Three moments to split",
        caption: "At payment authorization, after the purchase, and automatically.",
      }}
      related={relatedArticles("/case-study/installments")}
    >
      <Section heading="Two weak funnels, then a blocked third" tocLabel="The problem">
        <Body>
          About 70% of the target was meant to come from the two existing ways
          to split a credit-card purchase: after the purchase, converting at
          ~37%, and automatically, at ~5%. The other 30% depended on making new
          operations splittable — Bizum, bank transfers and debit. Midway
          through the year, those were blocked, so the growth had to come from
          the system we already had.
        </Body>
      </Section>

      <Section heading="People decided at checkout" tocLabel="The key shift">
        <Body>
          Repairing the two funnels was not going to close the gap, so I moved
          the work onto the decision itself: when people chose to split, and
          why.
        </Body>
        <Timeline items={SHIFT} />
        <Figure
          visual={AUTHORIZATION}
          alt="Payment authorization for 600 euros, with pay in full selected and the six-month and two-month plans offered below it. Next, six months selected with its cost opened: 673.56 euros in total, 73.56 euros of interest and nothing to pay today. Then the payment authorized, to be paid in six monthly payments of 112.26 euros"
          label="Authorize-and-split"
          caption="Plans at payment authorization, with pay in full still the default."
        />
        <Standout>
          The best place to offer a split was the moment people were already
          deciding how to pay.
        </Standout>
      </Section>

      <Section heading="Making the existing funnels convert" tocLabel="Decisions">
        <Body>
          The two existing funnels still carried most of the target, and two
          decisions moved them.
        </Body>
        <Points items={DECISIONS} />
        <Figure
          src={imgInstallmentsLabtesting}
          alt="Four selector patterns tested in the lab: circular slider, vertical card list, horizontal slider, and 2x2 card grid"
          label="Four selector patterns"
          caption="The vertical list won because plans could be compared at a glance."
        />
      </Section>

      <Section heading="+114%, all from the system we already had" tocLabel="Results">
        <Body>
          The year closed at +114% against a +100% target, without any of the
          blocked operations. Automatic-installment conversion rose from ~5% to
          ~49%. The uplift came from:
        </Body>
        <Points items={UPLIFT} />
      </Section>

      <Section heading="What this proves" tocLabel="Takeaways">
        <List items={PROOF} />
      </Section>

      <Notes items={NOTES} />
    </ResearchArticle>
  );
}

export default CaseStudyInstallments;
